/**
 * Game and round data model.
 *
 * The load-bearing decision here: every round is stored as its own record,
 * not folded into a running total. That is what makes the end-game
 * round-by-round summary possible, and it also gives you free correction of a
 * mis-scored round (edit the record, recompute totals) and a full audit trail
 * — which matters for trust in the leaderboard even though no real money is
 * ever involved.
 */

import type { BonusTile, Tile, Wind } from '../rules/tiles';
import type { Meld, PatternHit, Payment, PlayerId } from '../rules/types';

export interface Player {
  id: PlayerId;
  name: string;
  /** Starting seat. Seats rotate; the round record captures the actual seat. */
  seat: Wind;
}

export type RoundOutcome = 'win' | 'draw';

/**
 * What a round looked like when it was first recorded, kept once it has been corrected so a
 * dispute ("it said 4 fan and Theo +8 before") can always be answered. Captured on the FIRST
 * edit only and never overwritten, so it stays the true original however many times it is fixed.
 */
export interface RoundOriginal {
  winner?: PlayerId;
  discarder?: PlayerId;
  selfDraw?: boolean;
  fan?: number;
  effectiveFan?: number;
  isLimit?: boolean;
  tenpai?: PlayerId[];
  payments: Payment[];
}

/** Present only on a round that was corrected after it was recorded. */
export interface RoundEdit {
  /** When it was last corrected (epoch ms). */
  at: number;
  /** How many times it has been corrected. */
  count: number;
  original: RoundOriginal;
}

export interface RoundRecord {
  /** Monotonic within a game, starting at 1. */
  index: number;
  outcome: RoundOutcome;

  dealer: PlayerId;
  prevailingWind: Wind;

  // --- populated when outcome === 'win'
  winner?: PlayerId;
  winningTile?: string;
  selfDraw?: boolean;
  /** Present only on a discard win. */
  discarder?: PlayerId;

  /** Fan before the limit cap. */
  fan?: number;
  /** Fan actually paid out (capped). */
  effectiveFan?: number;
  /** Base value before dealer multiplier and payment split. */
  basePoints?: number;
  /** Which patterns fired, for the round-by-round breakdown. */
  patterns?: PatternHit[];
  isLimit?: boolean;
  /** Downscaled photo(s) used for tile recognition, if this round used it. */
  photos?: string[];
  /**
   * The hand as confirmed, kept for audit trail and so a tile-picture
   * rendering can be produced later even when there's no camera photo
   * (manual entry). Not used for re-scoring — the fan/patterns above are
   * already final.
   */
  concealed?: Tile[];
  melds?: Meld[];
  flowers?: BonusTile[];
  /** True if a pung or chow was claimed from a discard — see `HandInput.claimedSet`. */
  claimedSet?: boolean;

  // --- populated when outcome === 'draw'
  tenpai?: PlayerId[];

  /** Net change per player after every multiplier. Always sums to zero. */
  payments: Payment[];

  /** Epoch ms. Recorded for ordering and dispute resolution, never for scoring. */
  recordedAt: number;

  /** Set when the round was corrected after being recorded; shows as "Edited" and keeps the original result. */
  edited?: RoundEdit;
}

/**
 * Present on a game started in Leaderboard mode (steps 3-4 of the venue leaderboard; see
 * relay/leaderboard.ts). The game is otherwise an ordinary HKOS game: its `settings` are the
 * period's frozen house rules, snapshotted at start like any other (CLAUDE.md rule 5), so
 * nothing about scoring changes — this only records what the leaderboard needs to know.
 * Never holds a PIN: those are typed once at setup, sent to the relay, and dropped.
 */
export interface LeaderboardInfo {
  /** The relay's id for this game (from /leaderboard/start) — what the result is submitted against. */
  gameId: string;
  periodId: string;
  venue: string;
  /** 'zero_sum' (Serious-style) or 'winner_only' (Fun-style): which invariant the relay checks on the result. */
  settlement: 'zero_sum' | 'winner_only';
  /**
   * Seats that were already at their game limit when the game started: they play, but the result is not
   * added to their totals (the relay decided this at start and will not change its mind).
   */
  notCounted: { nickname: string; reason: 'daily_limit' | 'period_limit' }[];
  /** Epoch ms: when the relay accepted the start. */
  startedAt: number;
  /** Set once the result has reached the leaderboard. Absent until then; once set the game can no longer be reopened. */
  submittedAt?: number;
  /**
   * Set when the relay REFUSED the result (too few rounds, the game was replaced by a newer one, …). Not retried
   * automatically — the reason is shown to the host — and cleared if they reopen the game and end it again.
   */
  rejected?: { code: string; message: string };
}

export interface Game {
  /** Stable id for this game. */
  id: string;
  /**
   * Which physical table this game belongs to. Keying storage by table id is
   * what stops two tables in the same venue from mixing their rounds.
   */
  tableId: string;
  rulesetId: string;
  /** Ruleset-specific settings, snapshotted at game start so mid-game
   *  settings changes cannot retroactively rescore earlier rounds. */
  settings: unknown;
  players: Player[];
  rounds: RoundRecord[];
  startedAt: number;
  endedAt?: number;
  /**
   * The host chose "decide the seating with the dice" at setup: `players` was
   * entered in ANY order and the winds are assigned afterwards (see
   * `seating.ts`). Absent on every game saved before this option existed and
   * on every game whose seating was typed in — both behave exactly as before.
   */
  seatingByDice?: boolean;
  /** True from setup until the host assigns the winds. While true, no round can be recorded: the dealer is unknown. */
  seatingPending?: boolean;
  /** Set only for a Leaderboard-mode game — see `LeaderboardInfo`. */
  leaderboard?: LeaderboardInfo;
}

export function newGame(params: {
  id: string;
  tableId: string;
  rulesetId: string;
  settings: unknown;
  players: Player[];
  /** Start with the seating undecided — see `Game.seatingByDice`. */
  seatingByDice?: boolean;
  /** Mark this as a Leaderboard-mode game — see `LeaderboardInfo`. It is spread straight onto the game. */
  leaderboard?: LeaderboardInfo;
}): Game {
  if (params.players.length !== 4) {
    throw new Error(`A game needs exactly 4 players, got ${params.players.length}`);
  }
  const { seatingByDice, ...rest } = params;
  return {
    ...rest,
    rounds: [],
    startedAt: Date.now(),
    ...(seatingByDice ? { seatingByDice: true, seatingPending: true } : {}),
  };
}
